import React, { useState } from "react";
import { CicdFaqs } from "../../../components/faqData.js";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const KubernetesClusters = () => {
  const [actIndex, setActIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActIndex(index === actIndex ? null : index);
  };
  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      .scrollIntoView({ behavior: "smooth" });
  };

  const kubernetesServices = [
    {
      logo_images:"../assets/icons/kubernetes/service-1.png",
      title: "Cluster Setup",
      description:
        "Design and deploy production-ready Kubernetes clusters with high availability, multi-master configurations, and best practices. We configure control planes, etcd clusters, and worker nodes for optimal performance and reliability.",
    },
    {
      logo_images:"../assets/icons/kubernetes/service-2.png",
      title: "Node Management",
      description:
        "Manage Kubernetes nodes with automated provisioning, scaling, and maintenance. We configure node pools, taints, tolerations, and node selectors to optimize workload placement and resource utilization.",
    },
    {
      logo_images:"../assets/icons/kubernetes/service-3.png",
      title: "Deployments, Pods, Services",
      description:
        "Create and manage Kubernetes workloads including Deployments, StatefulSets, DaemonSets, and Jobs. We configure Services (ClusterIP, NodePort, LoadBalancer) for service discovery and load balancing across pods.",
    },
    {
      logo_images:"../assets/icons/kubernetes/service-4.png",
      title: "Ingress & Networking",
      description:
        "Implement Ingress controllers (NGINX, Traefik, Istio) for external traffic management, SSL/TLS termination, and path-based routing. We configure Network Policies for micro-segmentation and traffic control.",
    },
    {
      logo_images:"../assets/icons/kubernetes/service-5.png",
      title: "Secrets & ConfigMaps",
      description:
        "Manage application configuration and sensitive data with ConfigMaps and Secrets. We implement secure secret management with encryption at rest, integration with cloud key vaults, and RBAC policies.",
    },
    {
      logo_images:"../assets/icons/kubernetes/service-6.png",
      title: "Autoscaling (HPA)",
      description:
        "Configure Horizontal Pod Autoscaler (HPA) and Vertical Pod Autoscaler (VPA) based on CPU, memory, and custom metrics. We implement Cluster Autoscaler to dynamically add/remove nodes based on workload demand.",
    },
    {
      logo_images:"../assets/icons/kubernetes/service-7.png",
      title: "Cluster Monitoring",
      description:
        "Set up comprehensive monitoring and observability with Prometheus, Grafana, and cloud-native tools. We implement logging with Fluentd/ELK stack, distributed tracing, and alerting for proactive issue detection.",
    },
  ];

  const expertise = [
    "Certified Kubernetes Administrators (CKA) and Application Developers (CKAD)",
    "Multi-cloud Kubernetes deployments across EKS, AKS, GKE, and on-premises",
    "Advanced networking with service mesh (Istio, Linkerd) and CNI plugins",
    "Stateful application management with StatefulSets and persistent volumes",
    "GitOps workflows with ArgoCD and Flux for declarative deployments",
    "Security hardening with Pod Security Policies, Network Policies, and RBAC",
    "Performance optimization and cost reduction strategies",
    "Disaster recovery and backup solutions for Kubernetes clusters",
  ];

  const useCases = [
    {
      title: "Enterprise Microservices Platform",
      description:
        "Deployed a Kubernetes cluster managing 100+ microservices with auto-scaling, service mesh, and distributed tracing. Achieved 99.99% uptime, reduced deployment time by 90%, and improved resource utilization by 65%.",
    },
    {
      title: "Multi-Tenant SaaS Application",
      description:
        "Built a multi-tenant Kubernetes platform with namespace isolation, resource quotas, and network policies. Enabled secure tenant isolation while sharing cluster resources, reducing infrastructure costs by 50%.",
    },
    {
      title: "CI/CD Pipeline Automation",
      description:
        "Implemented GitOps workflows with ArgoCD on Kubernetes, automating deployments across 8 environments. Reduced manual deployment time from 4 hours to 15 minutes with automated rollback capabilities.",
    },
    {
      title: "High-Performance Data Processing",
      description:
        "Configured Kubernetes cluster with HPA and custom metrics for real-time data processing workloads. Achieved dynamic scaling from 10 to 500 pods based on queue depth, processing 10M+ events per day.",
    },
  ];

  const whyChooseUs = [
    "Kubernetes Certified Engineers (CKA, CKAD, CKS) with 10+ years of experience",
    "End-to-end services from cluster design to 24/7 managed operations",
    "Multi-cloud expertise across AWS EKS, Azure AKS, Google GKE, and self-managed clusters",
    "Cost optimization strategies reducing Kubernetes infrastructure spend by 30-50%",
    "Security-first approach with compliance-ready implementations (SOC 2, HIPAA, PCI-DSS)",
    "Automated monitoring, alerting, and incident response for production clusters",
  ];

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Expert Kubernetes consulting services in India. Certified K8s architects for seamless migration, scaling, and cloud-native security. Get a free audit today."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Kubernetes Consulting Services Provider in India |  Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-kubernetes"
        />
        <meta
          property="og:description"
          content="Expert Kubernetes consulting services in India. Certified K8s architects for seamless migration, scaling, and cloud-native security. Get a free audit today."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Kubernetes Consulting Services Provider in India |  Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Expert Kubernetes consulting services in India. Certified K8s architects for seamless migration, scaling, and cloud-native security. Get a free audit today."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-kubernetes"
        />
        <title>
        Kubernetes Consulting Services Provider in India |  Ayanshtech Solutions
        </title>
      </Helmet>
      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero Section */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Kubernetes Consulting Services India
              </h1>

              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Orchestrate and manage containerized applications at scale with
                Kubernetes, the industry-leading container orchestration platform.
                Ayanshtech Solutions delivers comprehensive Kubernetes cluster
                services, helping you deploy, scale, and manage containerized
                workloads efficiently and reliably.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Our certified Kubernetes engineers design, deploy, and optimize
                production-ready clusters that ensure high availability,
                auto-scaling, and seamless service discovery. From cluster setup
                and node management to deployments, networking, and monitoring,
                we help you leverage the full power of Kubernetes orchestration.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Whether you're building new cloud-native applications, migrating
                existing workloads to Kubernetes, or optimizing cluster performance,
                our team ensures best practices, security compliance, and
                operational excellence at every step.
              </p>

              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Get Started
              </button>
            </div>

            <div className="flex w-full md:w-1/2  justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/Kubernetes-banner.webp"
                alt="Kubernetes Clusters"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* Intro to Kubernetes */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Introduction to Kubernetes
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Kubernetes is an open-source container orchestration platform that
            automates the deployment, scaling, and management of containerized
            applications. Originally developed by Google and now maintained by the
            Cloud Native Computing Foundation (CNCF), Kubernetes has become the
            de facto standard for running containerized workloads in production.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Kubernetes provides powerful features including automatic scaling,
            self-healing, service discovery, load balancing, rolling updates, and
            secret management. It abstracts away the underlying infrastructure,
            allowing developers to focus on application logic while operations
            teams manage clusters efficiently.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            At Ayanshtech, we help organizations adopt Kubernetes to modernize
            their infrastructure, improve application reliability, and accelerate
            software delivery. Our comprehensive Kubernetes services cover
            everything from initial cluster setup to ongoing management,
            optimization, and troubleshooting.
          </p>
        </div> */}

        {/* Kubernetes Services */}
        <div className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Kubernetes Services
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Our comprehensive Kubernetes services cover all aspects of cluster
            management, from initial setup to production operations and
            optimization.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {kubernetesServices.map((service, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                  
                >
                  <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={service.logo_images} className="w-full rounded-2xl" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{service.title}</h3>
                  </div>
                  <p className="text-lg">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Kubernetes Expertise */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Our Kubernetes Expertise
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Ayanshtech's Kubernetes-certified team brings deep expertise across
              the entire Kubernetes ecosystem, delivering solutions that align
              with your business objectives and technical requirements.
            </p>
          </div>

          <div className="flex justify-center flex-col md:flex-row item-center mb-8">
            <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto space-y-3">
              {expertise.map((item, index) => (
                <li key={index} className="text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
            <div className="w-full md:w-1/2 lg:w-[40%]">
              <img src="../assets/images/devops-pages/Kubernetes-image.png" className="w-full h-auto object-contain" alt="" />
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            See how we've helped organizations achieve their container
            orchestration goals with Kubernetes clusters.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{useCase.title}</h3>
                  <p className="text-lg">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Ayanshtech for Kubernetes Clusters
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Partner with Ayanshtech for Kubernetes expertise that drives
              measurable business outcomes. Our proven methodology, certified
              engineers, and customer-first approach ensure successful Kubernetes
              adoption and cluster management.
            </p>
          </div>

          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Free Kubernetes Consultation Today!
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto p-6 py-20 w-full">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-8">
            FAQs
          </h2>
          {CicdFaqs.map((faq, index) => (
            <div key={index} className="border-b">
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left py-4 flex justify-between items-center focus:outline-none ${
                  actIndex === index
                    ? "bg-blue-600 text-white px-5 rounded-2xl"
                    : ""
                }`}
              >
                <span className="text-lg">{faq.question}</span>
                <span className="text-2xl">
                  {actIndex === index ? "-" : "+"}
                </span>
              </button>
              {actIndex === index && (
                <div className="p-4 text-gray-700">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div id="contact-section">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default KubernetesClusters;

